import { useState, useEffect, useCallback } from 'react';
import { useConnector } from '../contexts/ConnectorContext';
import type { BillOutRateData } from '../types/connector.types';
import { DatePickerInput } from './DatePickerInput';

interface RateItem {
  id: string;
  costGroup: string;
  billRate: number;
  costRate: number;
  effectiveFrom: string;
  effectiveTo: string | null;
  notes: string;
}

interface RateForm {
  costGroup: string;
  billRate: string;
  costRate: string;
  effectiveFrom: string;
  effectiveTo: string;
  notes: string;
}

const EMPTY_FORM: RateForm = {
  costGroup: '',
  billRate: '',
  costRate: '',
  effectiveFrom: '',
  effectiveTo: '',
  notes: '',
};

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function formatDate(iso: string | null | undefined): string {
  if (!iso || iso === '1970-01-01') return '';
  const [y, m, d] = iso.split('-');
  return `${d} ${MONTHS[parseInt(m, 10) - 1]} ${y}`;
}

function isCurrent(rate: RateItem): boolean {
  const today = new Date().toISOString().slice(0, 10);
  return rate.effectiveFrom <= today && (rate.effectiveTo === null || rate.effectiveTo >= today);
}

function fmtRate(n: number): string {
  return `£${n.toFixed(2)}`;
}


export function RatesAdmin() {
  const connector = useConnector();

  const [rates, setRates] = useState<RateItem[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [formError, setFormError] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<RateForm>(EMPTY_FORM);

  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await connector.getBillOutRates();
      setRates(data.map(r => ({
        id: r.id,
        costGroup: r.costGroup,
        billRate: r.billRate,
        costRate: r.costRate,
        effectiveFrom: r.effectiveFrom,
        effectiveTo: r.effectiveTo,
        notes: r.notes,
      })));
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load rates');
    } finally {
      setLoading(false);
    }
  }, [connector]);

  useEffect(() => { void loadData(); }, [loadData]);

  const existingGroups = [...new Set(rates.map(r => r.costGroup))].sort();

  const filtered = rates.filter(r =>
    !search ||
    r.costGroup.toLowerCase().includes(search.toLowerCase()) ||
    r.notes.toLowerCase().includes(search.toLowerCase())
  );

  const grouped = filtered.reduce<Record<string, RateItem[]>>((acc, r) => {
    (acc[r.costGroup] ??= []).push(r);
    return acc;
  }, {});
  const sortedGroups = Object.keys(grouped).sort();

  function openAdd() {
    setEditId(null);
    setForm(EMPTY_FORM);
    setFormError(false);
    setSaveError(null);
    setModalOpen(true);
  }

  function openEdit(id: string) {
    const r = rates.find(x => x.id === id);
    if (!r) return;
    setEditId(id);
    setForm({
      costGroup: r.costGroup,
      billRate: String(r.billRate),
      costRate: String(r.costRate),
      effectiveFrom: r.effectiveFrom,
      effectiveTo: r.effectiveTo ?? '',
      notes: r.notes,
    });
    setFormError(false);
    setSaveError(null);
    setModalOpen(true);
  }

  function validate(): boolean {
    return (
      form.costGroup.trim() !== '' &&
      form.billRate !== '' &&
      form.costRate !== '' &&
      !isNaN(Number(form.billRate)) &&
      !isNaN(Number(form.costRate)) &&
      Number(form.billRate) >= 0 &&
      Number(form.costRate) >= 0 &&
      form.effectiveFrom !== '' &&
      (form.effectiveTo === '' || form.effectiveTo >= form.effectiveFrom)
    );
  }

  async function save() {
    if (!validate()) { setFormError(true); return; }
    setSaving(true);
    setSaveError(null);
    try {
      const data: BillOutRateData = {
        costGroup: form.costGroup.trim(),
        billRate: Number(form.billRate),
        costRate: Number(form.costRate),
        effectiveFrom: form.effectiveFrom,
        effectiveTo: form.effectiveTo || null,
        notes: form.notes.trim(),
      };
      if (editId) {
        await connector.updateBillOutRate(editId, data);
        setRates(prev => prev.map(r => r.id === editId ? { ...r, ...data, effectiveTo: data.effectiveTo ?? null } : r));
      } else {
        const created = await connector.createBillOutRate(data);
        setRates(prev => [...prev, {
          id: created.id,
          costGroup: created.costGroup,
          billRate: created.billRate,
          costRate: created.costRate,
          effectiveFrom: created.effectiveFrom,
          effectiveTo: created.effectiveTo,
          notes: created.notes,
        }]);
      }
      setModalOpen(false);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Failed to save rate');
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete() {
    if (!deleteConfirmId) return;
    setDeleting(true);
    try {
      await connector.deleteBillOutRate(deleteConfirmId);
      setRates(prev => prev.filter(r => r.id !== deleteConfirmId));
      setDeleteConfirmId(null);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to delete rate');
    } finally {
      setDeleting(false);
    }
  }

  if (loading) {
    return (
      <>
        <div className="ts-topbar">
          <div className="topbar-l">
            <div className="crumb"><span>Admin</span><span className="sep">/</span><span className="cur">Bill-Out Rates</span></div>
          </div>
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 14 }}>
          Loading…
        </div>
      </>
    );
  }

  if (loadError) {
    return (
      <>
        <div className="ts-topbar">
          <div className="topbar-l">
            <div className="crumb"><span>Admin</span><span className="sep">/</span><span className="cur">Bill-Out Rates</span></div>
          </div>
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
          <div style={{ color: 'var(--error, #ef4444)', fontSize: 14 }}>{loadError}</div>
          <button className="btn btn-ghost" onClick={() => void loadData()}>Retry</button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="ts-topbar">
        <div className="topbar-l">
          <div className="crumb">
            <span>Admin</span><span className="sep">/</span><span className="cur">Bill-Out Rates</span>
          </div>
        </div>
        <div className="topbar-r">
          <button className="btn btn-ghost" onClick={openAdd}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 13, height: 13, strokeWidth: 2 }}><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            Add Rate Period
          </button>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', background: 'var(--bg)', padding: '20px 28px' }}>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
          <div className="search-wrap">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
            <input placeholder="Search cost groups..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{filtered.length} rate period{filtered.length !== 1 ? 's' : ''}</span>
        </div>

        {sortedGroups.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)', fontSize: 14 }}>
            No rate periods found. Click <strong>Add Rate Period</strong> to create the first one.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {sortedGroups.map(group => (
              <div key={group} className="ts-card">
                <div className="ts-card-hdr" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span className="ts-card-title">{group}</span>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {grouped[group].length} period{grouped[group].length !== 1 ? 's' : ''}
                  </span>
                </div>

                <div style={{ overflowX: 'auto' }}>
                  <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border)' }}>
                        <th style={thStyle}>Status</th>
                        <th style={thStyle}>Bill Rate</th>
                        <th style={thStyle}>Cost Rate</th>
                        <th style={thStyle}>Effective From</th>
                        <th style={thStyle}>Effective To</th>
                        <th style={thStyle}>Notes</th>
                        <th style={{ ...thStyle, width: 80 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {grouped[group].map(r => {
                        const current = isCurrent(r);
                        return (
                          <tr key={r.id} style={{ borderBottom: '1px solid var(--border-light, #f0f2f5)' }}>
                            <td style={tdStyle}>
                              {current
                                ? <span className="badge badge-green">Current</span>
                                : r.effectiveTo && r.effectiveTo < new Date().toISOString().slice(0, 10)
                                  ? <span className="badge badge-muted">Expired</span>
                                  : <span className="badge badge-blue">Future</span>
                              }
                            </td>
                            <td style={{ ...tdStyle, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                              {fmtRate(r.billRate)}
                            </td>
                            <td style={{ ...tdStyle, fontVariantNumeric: 'tabular-nums' }}>
                              {fmtRate(r.costRate)}
                            </td>
                            <td style={tdStyle}>{formatDate(r.effectiveFrom)}</td>
                            <td style={tdStyle}>{formatDate(r.effectiveTo)}</td>
                            <td style={{ ...tdStyle, color: 'var(--text-muted)', maxWidth: 240, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                              {r.notes || '—'}
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>
                              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                                <button className="btn btn-ghost btn-sm" onClick={() => openEdit(r.id)}>Edit</button>
                                <button
                                  className="btn btn-ghost btn-sm"
                                  style={{ color: 'var(--error, #ef4444)' }}
                                  onClick={() => setDeleteConfirmId(r.id)}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      <div className={`adm-modal-bg${modalOpen ? ' open' : ''}`}>
        <div className="adm-modal wide">
          <div className="adm-modal-hdr">
            <span className="adm-modal-hdr-title">{editId ? 'Edit Rate Period' : 'Add Rate Period'}</span>
            <button className="adm-modal-x" onClick={() => setModalOpen(false)}>×</button>
          </div>
          <div className="adm-modal-body">

            <div className="form-field">
              <label>Cost Group <span className="req">*</span></label>
              <input
                className={`finput${formError && !form.costGroup.trim() ? ' error' : ''}`}
                list="cost-group-list"
                value={form.costGroup}
                onChange={e => setForm(f => ({ ...f, costGroup: e.target.value }))}
                placeholder="e.g. Senior Developer"
              />
              <datalist id="cost-group-list">
                {existingGroups.map(g => <option key={g} value={g} />)}
              </datalist>
            </div>

            <div className="form-row cols2">
              <div className="form-field">
                <label>Bill Rate (£/hr) <span className="req">*</span></label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className={`finput${formError && (form.billRate === '' || isNaN(Number(form.billRate))) ? ' error' : ''}`}
                  value={form.billRate}
                  onChange={e => setForm(f => ({ ...f, billRate: e.target.value }))}
                  placeholder="0.00"
                />
              </div>
              <div className="form-field">
                <label>Cost Rate (£/hr) <span className="req">*</span></label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className={`finput${formError && (form.costRate === '' || isNaN(Number(form.costRate))) ? ' error' : ''}`}
                  value={form.costRate}
                  onChange={e => setForm(f => ({ ...f, costRate: e.target.value }))}
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="form-row cols2">
              <div className="form-field">
                <label>Effective From <span className="req">*</span></label>
                <DatePickerInput
                  value={form.effectiveFrom}
                  onChange={v => setForm(f => ({ ...f, effectiveFrom: v }))}
                  hasError={formError && !form.effectiveFrom}
                />
              </div>
              <div className="form-field">
                <label>
                  Effective To
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 4 }}>(blank = open-ended)</span>
                </label>
                <DatePickerInput
                  value={form.effectiveTo}
                  onChange={v => setForm(f => ({ ...f, effectiveTo: v }))}
                  hasError={formError && !!form.effectiveTo && form.effectiveTo < form.effectiveFrom}
                />
              </div>
            </div>

            <div className="form-field">
              <label>Notes</label>
              <input
                className="finput"
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Optional — reason for rate change, contract ref, etc."
              />
            </div>

            <div className={`ferr${formError && (!form.costGroup.trim() || form.billRate === '' || form.costRate === '' || !form.effectiveFrom) ? ' show' : ''}`}>
              Please fill in all required fields.
            </div>
            <div className={`ferr${formError && form.effectiveTo && form.effectiveTo < form.effectiveFrom ? ' show' : ''}`}>
              Effective To must be on or after Effective From.
            </div>
            {saveError && <div className="ferr show">{saveError}</div>}

            <div className="adm-modal-footer">
              <button className="btn btn-ghost" onClick={() => setModalOpen(false)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={() => void save()} disabled={saving}>
                {saving ? 'Saving…' : editId ? 'Save Changes' : 'Add Rate Period'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirm Dialog */}
      <div className={`adm-modal-bg${deleteConfirmId ? ' open' : ''}`}>
        <div className="adm-modal">
          <div className="adm-modal-hdr">
            <span className="adm-modal-hdr-title">Delete Rate Period?</span>
            <button className="adm-modal-x" onClick={() => setDeleteConfirmId(null)}>×</button>
          </div>
          <div className="adm-modal-body">
            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0, lineHeight: 1.6 }}>
              This rate period will be permanently deleted. Historical time entries that already captured a bill/cost rate snapshot are not affected.
            </p>
            <div className="adm-modal-footer">
              <button className="btn btn-ghost" onClick={() => setDeleteConfirmId(null)} disabled={deleting}>Cancel</button>
              <button
                className="btn btn-primary"
                style={{ background: 'var(--red, #ef4444)', borderColor: 'var(--red, #ef4444)' }}
                onClick={() => void confirmDelete()}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const thStyle: React.CSSProperties = {
  padding: '8px 12px',
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 12px',
  fontSize: 13,
  verticalAlign: 'middle',
};
